<template>
  <PTransparentLoader :loading="loading">
    <CRow v-if="chargeback">
      <CCol lg="12">
        <div class="d-flex align-items-center mb-3">

          <CLink
            style="color: #100F23"
            class="mr-4"
            @click="goBack"
          >
            <CIcon size="custom-size" :width="18" name="cipArrowNarrowLeft" class="mr-2"/>
            <translate translate-context="accounting.order">Back</translate>
          </CLink>
          <h1 class="main-header mb-0">
            <translate translate-context="accounting.orderDetail">Order</translate>
            {{ chargeback.order_number || chargeback.id }}
          </h1>
        </div>


        <CCard class="mb-3">
          <CCardHeader class="border-0 px-4 pb-1 pt-4">
            <div class="d-flex align-items-center font-weight-medium">
              <CIcon name="cipShoppingBag01" size="custom-size" :width="18" class="mr-2"/>
              <translate translate-context="accounting.movement.refund">Order information</translate>
            </div>
          </CCardHeader>
          <CCardBody>
            <CRow>
              <CCol md="4">
                <div class="info-cell">
                  <div class="info-label">
                    <translate translate-context="chargeback.detail">Order Number</translate>
                  </div>
                  <CLink
                    class="font-weight-medium"
                    v-if="chargeback.order_id && orderRoute"
                    :to="orderRoute"
                  >
                    {{ chargeback.order_number }}
                  </CLink>
                  <div v-else class="font-weight-medium">{{ chargeback.order_number || '-' }}</div>
                </div>
              </CCol>
              <CCol md="4">
                <div class="info-cell">
                  <div class="info-label">
                    <translate translate-context="chargeback.detail">Order Item</translate>
                  </div>
                  <CLink
                    class="font-weight-medium"
                    v-if="chargeback.order_item_id && orderItemRoute"
                    :to="orderItemRoute"
                  >
                    {{ chargeback.order_item_id }}
                  </CLink>
                  <span v-else>{{ chargeback.order_item_id || '-' }}</span>
                </div>
              </CCol>
              <CCol md="4">
                <div class="info-cell">
                  <div class="info-label">
                    <translate translate-context="chargeback.detail">Event</translate>
                  </div>
                  <router-link
                    v-if="chargeback.pica_service_name && chargeback.pica_service_id"
                    class="font-weight-medium"
                    :to="{ name: 'picaServiceRedirect', params: { picaServiceId: chargeback.pica_service_id } }"
                  >
                    {{ chargeback.pica_service_name }}
                  </router-link>
                  <div v-else class="font-weight-medium">{{ chargeback.pica_service_name || '-' }}</div>
                </div>
              </CCol>
            </CRow>
            <CRow>
              <CCol md="4">
                <div class="info-cell">
                  <div class="info-label">
                    <translate translate-context="chargeback.detail">Effective Date</translate>
                  </div>
                  <div class="font-weight-medium">{{ chargeback.effective_date|formatDateTime('L LT') }}</div>
                </div>
              </CCol>
              <CCol md="4" v-if="hasScopePerm('accounting.chargeback.fields.user_email')">
                <div class="info-cell">
                  <div class="info-label">
                    <translate translate-context="chargeback.detail">User Email</translate>
                  </div>
                  <div class="font-weight-medium">{{ chargeback.user_email || '-' }}</div>
                </div>
              </CCol>
              <CCol md="4" v-if="chargeback.chargeback_type && hasScopePerm('accounting.chargeback.fields.chargeback_type')">
                <div class="info-cell">
                  <div class="info-label">
                    <translate translate-context="chargeback.detail">Dispute Type</translate>
                  </div>
                  <CBadge color="primary" class="badge-outline">{{ chargeback.chargeback_type.name }}</CBadge>
                </div>
              </CCol>
            </CRow>
          </CCardBody>
        </CCard>


        <CCard class="mb-3" v-if="hasAnyDisputeField">
          <CCardHeader class="border-0 px-4 pb-1 pt-4">
            <div class="d-flex align-items-center font-weight-medium">
              <CIcon name="cipInfo" size="custom-size" :width="18" class="mr-2"/>
              <translate translate-context="chargeback.detail">Dispute Info</translate>
            </div>
          </CCardHeader>
          <CCardBody>
            <CRow>
              <CCol md="4" v-if="hasScopePerm('accounting.chargeback.fields.dispute_status')">
                <div class="info-cell">
                  <div class="info-label">
                    <translate translate-context="chargeback.detail">Dispute Status</translate>
                  </div>
                  <CBadge v-if="chargeback.dispute_status" :color="getDisputeStatusColor(chargeback.dispute_status)" class="badge-outline">
                    {{ getDisputeStatusLabel(chargeback.dispute_status) }}
                  </CBadge>
                  <span v-else class="text-muted">-</span>
                </div>
              </CCol>
              <CCol md="4" v-if="hasScopePerm('accounting.chargeback.fields.dispute_opened_at')">
                <div class="info-cell">
                  <div class="info-label">
                    <translate translate-context="chargeback.detail">Dispute Opened</translate>
                  </div>
                  <div class="font-weight-medium">
                    <span v-if="chargeback.dispute_opened_at">{{ chargeback.dispute_opened_at|formatDateTime('L LT') }}</span>
                    <span v-else class="text-muted">-</span>
                  </div>
                </div>
              </CCol>
              <CCol md="4" v-if="hasScopePerm('accounting.chargeback.fields.dispute_resolved_at')">
                <div class="info-cell">
                  <div class="info-label">
                    <translate translate-context="chargeback.detail">Dispute Resolved</translate>
                  </div>
                  <div class="font-weight-medium">
                    <span v-if="chargeback.dispute_resolved_at">{{ chargeback.dispute_resolved_at|formatDateTime('L LT') }}</span>
                    <span v-else class="text-muted">-</span>
                  </div>
                </div>
              </CCol>
            </CRow>
            <CRow v-if="hasScopePerm('accounting.chargeback.fields.dispute_status_changed_at')">
              <CCol md="4">
                <div class="info-cell">
                  <div class="info-label">
                    <translate translate-context="chargeback.detail">Status Changed</translate>
                  </div>
                  <div class="font-weight-medium">
                    <span v-if="chargeback.dispute_status_changed_at">{{ chargeback.dispute_status_changed_at|formatDateTime('L LT') }}</span>
                    <span v-else class="text-muted">-</span>
                  </div>
                </div>
              </CCol>
            </CRow>
          </CCardBody>
        </CCard>

        <!-- Related Records -->

        <div class="detail-section" v-if="chargeback.related_records && chargeback.related_records.length">
          <div class="detail-section__header">
            <CIcon name="cil-link" class="mr-2"/>
            <translate translate-context="chargeback.detail">Related Records</translate>
            <span class="text-muted small ml-2">
              (<translate
              translate-context="chargeback.detail"
              :translate-params="{count: chargeback.related_records.length + 1}"
            >%{count} items in this order</translate>)
            </span>
          </div>
          <div class="mt-3">
            <div
              v-for="record in chargeback.related_records"
              :key="record.id"
              class="related-record"
            >
              <div class="d-flex justify-content-between align-items-center">
                <div>
                  <router-link
                    :to="getRecordRoute(record.id)"
                    class="reference-link"
                  >
                    <translate translate-context="chargeback.detail">Order Item</translate>
                    #{{ record.order_item_id }}
                  </router-link>
                  <span class="text-muted ml-2" v-if="record.pica_service_name">
                    {{ record.pica_service_name }}
                  </span>
                </div>
                <strong>{{ formatAmount(record.chargeback_amount, record.currency || chargeback.currency) }}</strong>
              </div>
            </div>
          </div>
        </div>

        <CCard class="mb-3">
          <CCardHeader class="border-0 px-4 pb-1 pt-4">
            <div class="d-flex align-items-center font-weight-medium">
              <CIcon name="cipMoney" size="custom-size" :width="18" class="mr-2"/>
              <translate translate-context="chargeback.detail">Amounts</translate>
            </div>
          </CCardHeader>
          <CCardBody>
            <CRow>
              <CCol md="4" v-if="hasScopePerm('accounting.chargeback.fields.chargeback_amount')">
                <div class="info-cell">
                  <div class="info-label">
                    <translate translate-context="chargeback.detail">Dispute Amount</translate>
                  </div>
                  <div class="font-weight-medium">{{ chargeback.chargeback_amount|currency(chargeback.currency) }}</div>
                </div>
              </CCol>
              <CCol md="4" v-if="hasScopePerm('accounting.chargeback.fields.dispute_fee_amount')">
                <div class="info-cell">
                  <div class="info-label">
                    <translate translate-context="chargeback.detail">Dispute Fee</translate>
                  </div>
                  <div class="font-weight-medium">{{
                      chargeback.dispute_fee_amount|currency(chargeback.currency)
                    }}
                  </div>
                </div>
              </CCol>
              <CCol md="4" v-if="hasScopePerm('accounting.chargeback.fields.total_loss')">
                <div class="info-cell">
                  <div class="info-label">
                    <translate translate-context="chargeback.detail">Total Loss</translate>
                  </div>
                  <div class="text-danger font-weight-medium">{{ totalLoss|currency(chargeback.currency) }}</div>
                </div>
              </CCol>
            </CRow>
            <CRow>
              <CCol md="4" v-if="hasScopePerm('accounting.chargeback.fields.photographer_loss')">
                <div class="info-cell">
                  <div class="info-label">
                    <translate translate-context="chargeback.detail">Photographer Loss</translate>
                  </div>
                  <div class="font-weight-medium">{{ chargeback.photographer_loss|currency(chargeback.currency) }}</div>
                </div>
              </CCol>
              <CCol md="4" v-if="hasScopePerm('accounting.chargeback.fields.pica_loss')">
                <div class="info-cell">
                  <div class="info-label">
                    <translate translate-context="chargeback.detail">Pica Loss</translate>
                  </div>
                  <div class="font-weight-medium">{{ chargeback.pica_loss|currency(chargeback.currency) }}</div>
                </div>
              </CCol>
              <CCol md="4" v-if="hasScopePerm('accounting.chargeback.fields.organizer_loss')">
                <div class="info-cell">
                  <div class="info-label">
                    <translate translate-context="chargeback.detail">Organizer Loss</translate>
                  </div>
                  <div class="font-weight-medium">{{ chargeback.organizer_loss|currency(chargeback.currency) }}</div>
                </div>
              </CCol>
            </CRow>
          </CCardBody>
        </CCard>

        <!-- Notes -->
        <CCard class="mb-3" v-if="chargeback.reason && hasScopePerm('accounting.chargeback.fields.reason')">
          <CCardHeader class="border-0 px-4 pb-1 pt-4">
            <div class="d-flex align-items-center font-weight-medium">
              <CIcon name="cipDoc" size="custom-size" :width="18" class="mr-2"/>
              <translate translate-context="chargeback.detail">Notes</translate>
            </div>
          </CCardHeader>
          <CCardBody>
            <div class="info-cell-highlight">
              {{ chargeback.reason }}
            </div>
          </CCardBody>
        </CCard>

        <CCard class="mb-3" v-if="hasScopePerm('accounting.chargeback.fields.created_by_email')">
          <CCardHeader class="border-0 px-4 pb-1 pt-4">
            <div class="d-flex align-items-center font-weight-medium">
              <CIcon name="cipInfo" size="custom-size" :width="18" class="mr-2"/>
              <translate translate-context="chargeback.detail">Creation Info</translate>
            </div>
          </CCardHeader>
          <CCardBody>
            <CRow>
              <CCol md="4">
                <div class="info-cell">
                  <div class="info-label">
                    <translate translate-context="chargeback.detail">Created by</translate>
                  </div>
                  <div class="font-weight-medium">
                    {{ chargeback.created_by_email || '-' }}
                  </div>
                </div>
              </CCol>
            </CRow>
          </CCardBody>
        </CCard>

        <!-- Entries Breakdown -->
        <LedgerEntriesTable
          :entries="normalizedEntries"
          :show-filters="true"
          v-if="normalizedEntries.length"
        />
      </CCol>
    </CRow>
  </PTransparentLoader>
</template>

<script>
import {mapActions, mapGetters} from 'vuex'
import PTransparentLoader from '@/domain/core/components/PTransparentLoader.vue'
import LedgerEntriesTable from '@/domain/accounting/components/LedgerEntriesTable.vue'
import {getDetailRouteParams, getMovementDetailRouteParams, getPermissionPrefix, TRANSACTION_TYPES, MOVEMENT_TYPES} from '../constants'
import {normalizeEntry, DISPUTE_STATUS_LABELS, DISPUTE_STATUS_COLORS} from './consts'
import ConfigScope from "@/domain/core/constant/configScope"
export default {
  name: 'ChargebackDetail',
  components: {
    PTransparentLoader,
    LedgerEntriesTable,
  },
  data() {
    return {
      loading: true
    }
  },
  async mounted() {
    await this.setScope(this.scope)
    await this.fetchScopedConfig()
    await this.loadChargeback()
  },
  computed: {
    ...mapGetters('accounting/chargeback', ['chargeback']),

    scopeType() {
      return this.$route.meta.scopeType || 'global'
    },

    scopeId() {
      if (this.scopeType === 'pica-service') {
        return this.$route.params.picaServiceId
      } else if (this.scopeType === 'contract-manager') {
        return this.$route.params.contractManagerId
      }
      return null
    },

    scope() {
      return {
        type: this.scopeType,
        id: this.scopeId ? parseInt(this.scopeId) : null
      }
    },

    listRouteName() {
      if (this.scopeType === 'contract-manager') return 'contractManager.chargebacks'
      if (this.scopeType === 'pica-service') return 'event.chargebacks'
      return 'admin.chargebacks'
    },

    permissionPrefix() {
      return getPermissionPrefix(this.scopeType)
    },

    orderRoute() {
      if (!this.chargeback || !this.chargeback.order_id) return null
      if (this.scope.type !== 'global') return null
      if (!this.hasPerm(`${this.permissionPrefix}.accounting.order.detail`)) return null
      return getDetailRouteParams(TRANSACTION_TYPES.ORDER, this.chargeback.order_id, this.scope)
    },

    orderItemRoute() {
      if (!this.chargeback || !this.chargeback.order_item_id) return null
      // CM/PS use movement routes, admin uses transaction routes
      if (this.scope.type === 'contract-manager' || this.scope.type === 'pica-service') {
        return getMovementDetailRouteParams(MOVEMENT_TYPES.ORDER_ITEM, this.chargeback.order_item_id, this.scope)
      }
      return getDetailRouteParams(TRANSACTION_TYPES.ORDER_ITEM, this.chargeback.order_item_id, this.scope)
    },

    totalLoss() {
      if (!this.chargeback) return 0
      return parseFloat(this.chargeback.total_loss) || 0
    },

    hasAnyDisputeField() {
      return this.hasScopePerm('accounting.chargeback.fields.dispute_status')
        || this.hasScopePerm('accounting.chargeback.fields.dispute_opened_at')
        || this.hasScopePerm('accounting.chargeback.fields.dispute_resolved_at')
        || this.hasScopePerm('accounting.chargeback.fields.dispute_status_changed_at')
    },

    normalizedEntries() {
      if (!this.chargeback || !this.chargeback.entries) return []
      return this.chargeback.entries.map(normalizeEntry)
    },
  },
  methods: {
    ...mapActions('accounting', ['setScope']),
    ...mapActions('accounting/chargeback', ['fetchChargeback']),
    ...mapActions('login', ['fetchConfig']),

    hasScopePerm(permission) {
      if (!this.scope || !this.scope.type) return false

      let fullPermission
      if (this.scope.type === 'pica-service') {
        fullPermission = `pica_service.${permission}`
      } else if (this.scope.type === 'contract-manager') {
        fullPermission = `contract_manager.${permission}`
      } else if (this.scope.type === 'global') {
        fullPermission = `admin.${permission}`
      } else {
        return false
      }

      return this.hasPerm(fullPermission)
    },

    async fetchScopedConfig() {
      try {
        if (this.scopeType === 'pica-service') {
          await this.fetchConfig({
            type: 'accounting_pica_service',
            id: this.scopeId,
            scope: ConfigScope.PICA_SERVICE
          })
        } else if (this.scopeType === 'contract-manager') {
          await this.fetchConfig({
            type: 'accounting_contract_manager',
            id: this.scopeId
          })
        } else {
          await this.fetchConfig({
            type: 'accounting_global'
          })
        }
      } catch (e) {
        // Config endpoint may not exist yet
      }
    },

    goBack() {
      const params = {}
      if (this.scopeType === 'contract-manager' && this.scopeId) {
        params.contractManagerId = this.scopeId
      } else if (this.scopeType === 'pica-service' && this.scopeId) {
        params.picaServiceId = this.scopeId
      }
      this.$router.push({name: this.listRouteName, params})
    },

    getRecordRoute(recordId) {
      const params = {id: recordId}
      if (this.scopeType === 'contract-manager' && this.scopeId) {
        params.contractManagerId = this.scopeId
      } else if (this.scopeType === 'pica-service' && this.scopeId) {
        params.picaServiceId = this.scopeId
      }
      return {name: this.listRouteName + '.detail', params}
    },

    async loadChargeback() {
      this.loading = true
      try {
        await this.fetchChargeback(this.$route.params.id)
      } finally {
        this.loading = false
      }
    },

    formatDate(dateString) {
      if (!dateString) return '-'
      return new Date(dateString).toLocaleDateString()
    },

    formatDateTime(dateString) {
      if (!dateString) return '-'
      return new Date(dateString).toLocaleString()
    },

    formatAmount(amount, currency) {
      const num = parseFloat(amount) || 0
      return `${num.toFixed(2)} ${currency || ''}`
    },

    getDisputeStatusLabel(status) {
      return DISPUTE_STATUS_LABELS[status] || status
    },

    getDisputeStatusColor(status) {
      return DISPUTE_STATUS_COLORS[status] || 'secondary'
    },
  }
}
</script>

<style scoped>
.detail-section {
  padding: 20px 0;
  border-bottom: 1px solid #e4e7ea;
}

.detail-section:last-of-type {
  border-bottom: none;
}

.detail-section__header {
  font-size: 1rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  color: #333;
}

.description-block {
  background: #f8f9fa;
  border-radius: 6px;
  padding: 12px 16px;
  color: #4f5d73;
  line-height: 1.5;
}

.reference-link {
  color: #e67e22;
  text-decoration: underline;
  font-weight: 500;
}

.reference-link:hover {
  color: #d35400;
}

.related-record {
  padding: 10px 0;
  border-bottom: 1px solid #f0f0f0;
}

.related-record:last-child {
  border-bottom: none;
}
</style>
